import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { MantineProvider, createTheme, mergeThemeOverrides } from "@mantine/core";
import { customTheme } from "@/mantine-theme";
import { brandColorTuple } from "@/utils/brandColors";

export interface CompanyBranding {
  primaryColor?: string;
  borderRadius?: number;
}

interface BrandingContextType {
  branding: CompanyBranding | null;
  setBranding: (b: CompanyBranding | null) => void;
}

const BrandingContext = createContext<BrandingContextType>({
  branding: null,
  setBranding: () => {},
});

export const useBranding = () => useContext(BrandingContext);

export function BrandingProvider({ children }: { children: React.ReactNode }) {
  const [branding, setBranding] = useState<CompanyBranding | null>(null);

  const theme = useMemo(() => {
    if (!branding) return customTheme;
    return mergeThemeOverrides(
      customTheme,
      createTheme({
        ...(branding.primaryColor && {
          primaryColor: "brand",
          colors: { brand: brandColorTuple(branding.primaryColor) },
        }),
        ...(branding.borderRadius != null && {
          defaultRadius: `${branding.borderRadius}px`,
        }),
      })
    );
  }, [branding]);

  // When white-label branding sets a primary color, point the nav accent vars at
  // the brand's Mantine primary. Without a brand color the vars stay unset and the
  // nav falls back to the default Reviver aqua highlight (see SidebarLayout styles).
  useEffect(() => {
    const root = document.documentElement;
    if (branding?.primaryColor) {
      root.style.setProperty("--nav-active-bg", "var(--mantine-primary-color-light)");
      root.style.setProperty("--nav-active-border", "var(--mantine-primary-color-filled)");
    } else {
      root.style.removeProperty("--nav-active-bg");
      root.style.removeProperty("--nav-active-border");
    }
  }, [branding?.primaryColor]);

  return (
    <BrandingContext.Provider value={{ branding, setBranding }}>
      <MantineProvider theme={theme}>{children}</MantineProvider>
    </BrandingContext.Provider>
  );
}
